import { useState, useEffect, useCallback } from 'react';
import { c, money, STATUS_META, inputStyle } from './cajeroShared';

function DocList({ token, type, typeLabel, onBack }) {
  const [qInput, setQInput] = useState('');
  const [statusInput, setStatusInput] = useState('');
  // Los filtros SOLO se aplican al presionar "Buscar" (no en cada tecla).
  const [applied, setApplied] = useState({ q: '', status: '', page: 1 });
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);

  // Exportar a CSV: rango de fechas + nivel de detalle (global o por ítem).
  const [showExport, setShowExport] = useState(false);
  const [exportMode, setExportMode] = useState('global'); // 'global' | 'items'
  const [exportFrom, setExportFrom] = useState('');
  const [exportTo, setExportTo] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');

  const authH = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ type, page: String(applied.page) });
      if (applied.q) params.set('q', applied.q);
      if (applied.status) params.set('status', applied.status);
      const r = await fetch(`/api/cajero/docs?${params}`, { headers: authH });
      const data = await r.json();
      setRows(data.data || []);
      setTotal(data.total || 0);
      if (data.statuses) setStatuses(data.statuses);
    } catch { setMsg({ ok: false, text: 'Error al cargar' }); }
    finally { setLoading(false); }
  }, [type, applied]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const search = () => setApplied({ q: qInput.trim(), status: statusInput, page: 1 });

  const changeStatus = async (row, status) => {
    try {
      const r = await fetch(`/api/cajero/docs/${type}/${row.id}/status`, {
        method: 'PUT', headers: { ...authH, 'Content-Type': 'application/json' }, body: JSON.stringify({ status }),
      });
      if (r.ok) setRows((prev) => prev.map((x) => (x.id === row.id ? { ...x, status } : x)));
      else { const d = await r.json(); setMsg({ ok: false, text: d.message || 'No se pudo actualizar' }); }
    } catch { setMsg({ ok: false, text: 'Error al actualizar' }); }
  };

  const print = async (row) => {
    try {
      const r = await fetch(`/api/cajero/docs/${type}/${row.id}/print`, { headers: authH });
      const d = await r.json();
      if (r.ok && d.url) window.open(d.url, '_blank');
      else setMsg({ ok: false, text: d.message || 'No se pudo imprimir' });
    } catch { setMsg({ ok: false, text: 'Error al imprimir' }); }
  };

  // ── Exportar a CSV ──
  const openExport = () => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const first = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-01`;
    const last = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate())}`;
    setExportFrom(first);
    setExportTo(last);
    setExportMode('global');
    setExportError('');
    setShowExport(true);
  };

  const doExport = async () => {
    if (!exportFrom || !exportTo) return setExportError('Selecciona ambas fechas');
    if (exportFrom > exportTo) return setExportError('La fecha inicial no puede ser mayor que la final');
    setExporting(true); setExportError('');
    try {
      const params = new URLSearchParams({ type, mode: exportMode, from: exportFrom, to: exportTo });
      const r = await fetch(`/api/cajero/docs/export?${params}`, { headers: authH });
      if (!r.ok) {
        let text = 'No se pudo exportar';
        try { const d = await r.json(); text = d.message || text; } catch { /* respuesta no JSON */ }
        setExportError(text);
        return;
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${type}_${exportMode}_${exportFrom}_a_${exportTo}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      setShowExport(false);
    } catch { setExportError('No se pudo exportar'); }
    finally { setExporting(false); }
  };

  const totalPages = Math.max(1, Math.ceil(total / 20));

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '1.25rem 1rem 4rem' }}>
      <button onClick={onBack} style={btnOutline}>← Volver</button>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px', margin: '10px 0 16px' }}>
        <h1 style={{ color: c.primary, fontSize: '1.4rem', margin: 0 }}>{typeLabel}</h1>
        <button onClick={openExport} style={btnOutline}>⬇ Exportar CSV</button>
      </div>

      {/* Buscador + filtros (aplican al presionar Buscar) */}
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '16px' }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <label style={lbl}>Buscar (No., cliente o documento)</label>
          <input style={inputStyle} value={qInput} onChange={(e) => setQInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && search()} placeholder="Escribe y presiona Buscar" />
        </div>
        <div style={{ minWidth: 160 }}>
          <label style={lbl}>Estado</label>
          <select style={inputStyle} value={statusInput} onChange={(e) => setStatusInput(e.target.value)}>
            <option value="">Todos</option>
            {statuses.map((s) => <option key={s} value={s}>{STATUS_META[s]?.label || s}</option>)}
          </select>
        </div>
        <button onClick={search} style={btn(c.primary, c.background)}>Buscar</button>
        {(applied.q || applied.status) && (
          <button onClick={() => { setQInput(''); setStatusInput(''); setApplied({ q: '', status: '', page: 1 }); }} style={btnOutline}>Limpiar</button>
        )}
      </div>

      {msg && <p style={{ color: msg.ok ? c.success : c.error, fontSize: '0.85rem' }}>{msg.text}</p>}

      <div style={{ overflowX: 'auto', border: `1px solid ${c.border}`, borderRadius: '12px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640, fontSize: '0.88rem' }}>
          <thead>
            <tr style={{ background: c.surface, color: c.textMuted, textAlign: 'left' }}>
              <th style={th}>No.</th><th style={th}>Cliente</th><th style={{ ...th, textAlign: 'right' }}>Total</th>
              <th style={th}>Fecha</th><th style={th}>Estado</th><th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ ...td, textAlign: 'center', color: c.textMuted }}>Cargando…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={6} style={{ ...td, textAlign: 'center', color: c.textMuted }}>Sin resultados</td></tr>
            ) : rows.map((row) => {
              const meta = STATUS_META[row.status] || {};
              return (
                <tr key={row.id} style={{ borderTop: `1px solid ${c.border}` }}>
                  <td style={td}>{row.full_number}</td>
                  <td style={td}>{row.customer_name || '—'}{row.customer_doc ? <div style={{ color: c.textMuted, fontSize: '0.78rem' }}>{row.customer_doc}</div> : null}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{money(row.total)}</td>
                  <td style={{ ...td, color: c.textMuted, whiteSpace: 'nowrap' }}>{new Date(row.created_at).toLocaleDateString('es-CO')}</td>
                  <td style={td}>
                    <select value={row.status} onChange={(e) => changeStatus(row, e.target.value)}
                      style={{ ...inputStyle, width: 'auto', padding: '5px 8px', color: meta.color || c.text, borderColor: meta.color || c.border, fontWeight: 600 }}>
                      {statuses.map((s) => <option key={s} value={s} style={{ color: c.text }}>{STATUS_META[s]?.label || s}</option>)}
                    </select>
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    <button onClick={() => print(row)} style={btnOutline}>🖨 Imprimir</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', alignItems: 'center', marginTop: '16px' }}>
          <button onClick={() => setApplied((a) => ({ ...a, page: Math.max(1, a.page - 1) }))} disabled={applied.page <= 1} style={btnOutline}>‹</button>
          <span style={{ color: c.textMuted, fontSize: '0.85rem' }}>Página {applied.page} de {totalPages}</span>
          <button onClick={() => setApplied((a) => ({ ...a, page: Math.min(totalPages, a.page + 1) }))} disabled={applied.page >= totalPages} style={btnOutline}>›</button>
        </div>
      )}

      {/* ── Modal de exportación ── */}
      {showExport && (
        <div style={overlay} onClick={() => !exporting && setShowExport(false)}>
          <div style={modalBox} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ color: c.primary, fontSize: '1.15rem', margin: '0 0 4px' }}>Exportar {typeLabel.toLowerCase()}</h2>
            <p style={{ color: c.textMuted, fontSize: '0.83rem', margin: '0 0 18px' }}>Elige qué exportar y el rango de fechas. Se descargará un archivo CSV.</p>

            <label style={lbl}>¿Qué deseas exportar?</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', margin: '4px 0 16px' }}>
              <label style={radioRow(exportMode === 'global')}>
                <input type="radio" name="exportMode" checked={exportMode === 'global'} onChange={() => setExportMode('global')} />
                <span>
                  <span style={{ fontWeight: 600 }}>Solo documentos (global)</span>
                  <span style={{ display: 'block', color: c.textMuted, fontSize: '0.78rem' }}>Datos del cliente y totales: subtotal, IVA, retefuente, impuesto al consumo y total.</span>
                </span>
              </label>
              <label style={radioRow(exportMode === 'items')}>
                <input type="radio" name="exportMode" checked={exportMode === 'items'} onChange={() => setExportMode('items')} />
                <span>
                  <span style={{ fontWeight: 600 }}>Detallado por ítem vendido</span>
                  <span style={{ display: 'block', color: c.textMuted, fontSize: '0.78rem' }}>Cada ítem, a qué documento pertenece y la información del cliente en cada fila.</span>
                </span>
              </label>
            </div>

            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 130 }}>
                <label style={lbl}>Desde</label>
                <input type="date" style={inputStyle} value={exportFrom} onChange={(e) => setExportFrom(e.target.value)} />
              </div>
              <div style={{ flex: 1, minWidth: 130 }}>
                <label style={lbl}>Hasta</label>
                <input type="date" style={inputStyle} value={exportTo} onChange={(e) => setExportTo(e.target.value)} />
              </div>
            </div>

            {exportError && <p style={{ color: c.error, fontSize: '0.83rem', margin: '12px 0 0' }}>{exportError}</p>}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button onClick={() => setShowExport(false)} disabled={exporting} style={btnOutline}>Cancelar</button>
              <button onClick={doExport} disabled={exporting} style={btn(c.primary, c.background)}>{exporting ? 'Exportando…' : 'Exportar'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const lbl = { display: 'block', fontSize: '0.75rem', color: c.textMuted, marginBottom: '4px' };
const th = { padding: '11px 12px', fontWeight: 600, whiteSpace: 'nowrap' };
const td = { padding: '10px 12px' };
const btnOutline = { background: 'none', border: `1px solid ${c.border}`, color: c.text, borderRadius: '7px', padding: '8px 13px', cursor: 'pointer', fontSize: '0.82rem', fontFamily: 'inherit' };
function btn(bg, fg) { return { background: bg, border: 'none', color: fg, borderRadius: '7px', padding: '9px 18px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, fontFamily: 'inherit' }; }

const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', zIndex: 1000 };
const modalBox = { background: c.surface, border: `1px solid ${c.border}`, borderRadius: '14px', padding: '24px', width: '100%', maxWidth: 460, boxSizing: 'border-box', maxHeight: '90vh', overflowY: 'auto' };
function radioRow(active) { return { display: 'flex', gap: '10px', alignItems: 'flex-start', cursor: 'pointer', border: `1px solid ${active ? c.primary : c.border}`, borderRadius: '9px', padding: '11px 13px', fontSize: '0.88rem', color: c.text }; }

export default DocList;
